import React, { useState, useEffect, useCallback } from "react";
import { Sparkles, Loader2, Calendar, Filter, Heart, Wand2, Camera, Dna } from "lucide-react";
import { API_URL } from "../../constants";
import AIImageWithDisclaimer from "../AIImageWithDisclaimer";

const CATEGORY_META = {
  invitation: { label: "Invitations", icon: Wand2, color: "#E83E8C" },
  decor:      { label: "House Decor", icon: Heart, color: "#7C3AED" },
  creator:    { label: "Creator Studio", icon: Camera, color: "#FF9F1C" },
  party_dna:  { label: "Party DNA", icon: Dna, color: "#10B981" },
};
const DAY_FILTERS = [
  { v: "7",  l: "7 days" },
  { v: "30", l: "30 days" },
  { v: "90", l: "3 months" },
  { v: "all", l: "All time" },
];

const MyCreationsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [days, setDays] = useState("all");
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url = `${API_URL}/api/customer/my-creations?category=${category}&days=${days}&limit=120`;
      const res = await fetch(url, { credentials: "include" });
      if (res.status === 401) {
        setError("Please log in to view your creations.");
        setItems([]);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) {
      setError(e.message || "Could not load creations");
    } finally {
      setLoading(false);
    }
  }, [category, days]);

  useEffect(() => { load(); }, [load]);

  const grouped = items.reduce((acc, it) => {
    const date = new Date(it.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    (acc[date] = acc[date] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="my-creations-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-black text-[#1E1B4B] flex items-center gap-2">
            <Sparkles className="text-[#E83E8C]" size={20} /> My Creations
          </h2>
          <p className="text-xs text-[#475569]">Every AI image you've made — invitations, decor, creator transformations, party DNA cards.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-200 p-3 space-y-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#475569] mr-1"><Filter size={11} className="inline mr-0.5" /> Category:</span>
          <button onClick={() => setCategory("all")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${category === "all" ? "bg-[#1E1B4B] text-white" : "bg-gray-100 text-[#475569] hover:bg-gray-200"}`}
            data-testid="cat-filter-all">All</button>
          {Object.entries(CATEGORY_META).map(([key, meta]) => {
            const Icon = meta.icon;
            return (
              <button key={key} onClick={() => setCategory(key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition inline-flex items-center gap-1 ${category === key ? "text-white shadow-md" : "bg-gray-100 text-[#475569] hover:bg-gray-200"}`}
                style={category === key ? { backgroundColor: meta.color } : {}}
                data-testid={`cat-filter-${key}`}>
                <Icon size={11} /> {meta.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#475569] mr-1"><Calendar size={11} className="inline mr-0.5" /> When:</span>
          {DAY_FILTERS.map(d => (
            <button key={d.v} onClick={() => setDays(d.v)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${days === d.v ? "bg-[#E83E8C] text-white" : "bg-gray-100 text-[#475569] hover:bg-gray-200"}`}
              data-testid={`day-filter-${d.v}`}>{d.l}</button>
          ))}
        </div>
      </div>

      {/* Loading / error / empty */}
      {loading && <div className="text-center py-12 text-[#475569]"><Loader2 className="animate-spin mx-auto mb-2" size={24} /> Loading your creations…</div>}
      {!loading && error && <div className="p-5 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
      {!loading && !error && items.length === 0 && (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <Sparkles size={28} className="text-[#E83E8C]/40 mx-auto mb-2" />
          <p className="text-sm font-bold text-[#1E1B4B]">Nothing here yet</p>
          <p className="text-xs text-[#475569] mt-1">Try DoozyGo Magic, Creator Studio, or AI Invitations to start creating.</p>
        </div>
      )}

      {/* Date-grouped grid */}
      {!loading && Object.entries(grouped).map(([date, list]) => (
        <div key={date}>
          <h4 className="text-[11px] font-bold uppercase tracking-widest text-[#475569] mb-2">{date} · {list.length} item{list.length !== 1 ? "s" : ""}</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {list.map(item => {
              const meta = CATEGORY_META[item.category] || CATEGORY_META.decor;
              const Icon = meta.icon;
              const thumb = item.thumbnail_b64
                ? `data:image/jpeg;base64,${item.thumbnail_b64}`
                : item.image_base64
                  ? `data:image/png;base64,${item.image_base64}`
                  : "";
              return (
                <button key={item.id} onClick={() => setSelected(item)}
                  className="group relative rounded-2xl overflow-hidden bg-gray-100 aspect-square hover:scale-[1.02] hover:shadow-lg transition-all"
                  data-testid={`creation-${item.id}`}>
                  {thumb ? (
                    <>
                      <img src={thumb} alt={item.category} className="w-full h-full object-cover" loading="lazy" />
                      <div className="pointer-events-none absolute inset-x-0 bottom-6 px-1.5 py-0.5 text-[7px] font-medium text-white text-center leading-tight bg-black/55 backdrop-blur-[2px]" data-testid="ai-image-disclaimer">
                        AI reference image. T&amp;C apply.
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400"><Icon size={28} /></div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 bg-gradient-to-t from-black/75 to-transparent">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold text-white" style={{ backgroundColor: meta.color }}>
                      <Icon size={9} /> {meta.label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 space-y-3">
              {(selected.image_base64 || selected.thumbnail_b64) && (
                <AIImageWithDisclaimer src={`data:image/${selected.image_base64 ? "png" : "jpeg"};base64,${selected.image_base64 || selected.thumbnail_b64}`}
                  alt="" className="w-full max-h-[60vh] object-contain rounded-2xl" />
              )}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-[10px] uppercase font-bold tracking-widest" style={{ color: (CATEGORY_META[selected.category] || {}).color }}>
                    {(CATEGORY_META[selected.category] || {}).label}
                  </p>
                  <p className="text-xs text-[#475569]">{new Date(selected.created_at).toLocaleString()}</p>
                </div>
                <button onClick={() => setSelected(null)} className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-xs font-bold text-[#1E1B4B]">Close</button>
              </div>
              {selected.metadata && Object.keys(selected.metadata).length > 0 && (
                <div className="pt-2 border-t border-gray-100 text-[11px] text-[#475569] space-y-0.5">
                  {Object.entries(selected.metadata).slice(0, 8).map(([k, v]) => (
                    <div key={k}><span className="font-bold capitalize">{k.replace(/_/g, " ")}:</span> {String(v).slice(0, 80)}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCreationsTab;
